"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchWithAuth } from "@/lib/auth";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PresenterSide {
    name: string | null;
    email: string | null;
    job_title: string | null;
    company: string | null;
    linkedin: string | null;
    bio: string | null;
    photo_url: string | null;
}

type SyncStatus = "match" | "drift" | "only_wp" | "only_db";
type MatchConfidence = "email" | "name_only";
type ConfidenceFilter = MatchConfidence;
type ImportFilter = "has_email" | "no_email";
type PhotoStatus = "wp_only" | "hub_only" | "both" | "neither";
type FieldKey = "name" | "email" | "job_title" | "company" | "linkedin" | "bio";
type FieldStatus = "match" | "mismatch" | "wp_only" | "db_only" | "neither";

interface PresenterDiff {
    name: string;
    sync_status: SyncStatus;
    drifted_fields: string[];
    field_status: Record<FieldKey, FieldStatus>;
    wp: PresenterSide | null;
    db: PresenterSide | null;
    wp_id: number | null;
    db_id: string | null;
    match_confidence: MatchConfidence | null;
    can_import?: boolean | null;
    existing_hub_account_id?: string | null;
    photo_status: PhotoStatus;
}

interface ImportResultItem {
    wp_id: number;
    name: string;
    action: "created" | "updated" | "skipped";
    reason?: string;
    user_id?: string;
}

interface ImportResult {
    created: number;
    updated: number;
    skipped: number;
    results: ImportResultItem[];
}

interface PushOverride {
    wp: Partial<PresenterSide>;
    clearedFields: string[];
    photoStatus?: PhotoStatus;
}

interface PushState {
    loading: boolean;
    error: string | null;
}

interface BulkPushResultItem {
    wp_id: number;
    status: "ok" | "error";
    detail?: string;
}

interface BulkPushSummary {
    ok: number;
    error: number;
    errors: { wp_id: number; name: string; detail?: string }[];
}

const FIELD_DEFS: { key: string; label: string }[] = [
    { key: "job_title", label: "Job Title" },
    { key: "company",   label: "Company" },
    { key: "linkedin",  label: "LinkedIn" },
    { key: "bio",       label: "Bio" },
];

const PUSHABLE_FIELDS = ["job_title", "company", "linkedin", "bio"];

const PHOTO_STATUS_CONFIG: Record<PhotoStatus, { label: string; className: string }> = {
    wp_only:  { label: "Missing on Hub — import available", className: "bg-blue-100 text-blue-700" },
    hub_only: { label: "Missing on WordPress",               className: "bg-amber-100 text-amber-700" },
    both:     { label: "Photo on both sides",                 className: "bg-emerald-100 text-emerald-700" },
    neither:  { label: "No photo on either side",             className: "bg-[#f1f5f9] text-[#94a3b8]" },
};

const PHOTO_STATUS_FILTER_OPTIONS: { label: string; value: PhotoStatus }[] = [
    { label: "Photo: WP Only",  value: "wp_only"  },
    { label: "Photo: Hub Only", value: "hub_only" },
    { label: "Photo: Both",     value: "both"     },
    { label: "Photo: Neither",  value: "neither"  },
];

interface ScanSummary {
    total_wp: number;
    total_db: number;
    matched: number;
    drift: number;
    only_wp: number;
    only_db: number;
    in_sync: boolean;
}

interface ScanResult {
    scanned_at: string;
    summary: ScanSummary;
    presenters: PresenterDiff[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<SyncStatus, { label: string; pill: string; dot: string; rowBg: string }> = {
    match:   { label: "Match",       pill: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", rowBg: "" },
    drift:   { label: "Field Drift", pill: "bg-amber-100 text-amber-700",    dot: "bg-amber-400",   rowBg: "bg-amber-50/60" },
    only_wp: { label: "WP Only",     pill: "bg-blue-100 text-blue-700",      dot: "bg-blue-500",    rowBg: "bg-blue-50/60" },
    only_db: { label: "DB Only",     pill: "bg-rose-100 text-rose-700",      dot: "bg-rose-500",    rowBg: "bg-rose-50/60" },
};

const SYNC_FILTER_OPTIONS: { label: string; value: SyncStatus }[] = [
    { label: "Match",    value: "match"   },
    { label: "Drift",    value: "drift"   },
    { label: "WP Only",  value: "only_wp" },
    { label: "DB Only",  value: "only_db" },
];

const CONFIDENCE_FILTER_OPTIONS: { label: string; value: ConfidenceFilter }[] = [
    { label: "Email Match",      value: "email"     },
    { label: "Name Match Only",  value: "name_only" },
];

const IMPORT_FILTER_OPTIONS: { label: string; value: ImportFilter }[] = [
    { label: "Has Email", value: "has_email" },
    { label: "No Email",  value: "no_email"  },
];

const FIELD_FILTER_OPTIONS: { label: string; value: FieldKey }[] = [
    { label: "Name",       value: "name"      },
    { label: "Email",      value: "email"     },
    { label: "Job Title",  value: "job_title" },
    { label: "Company",    value: "company"   },
    { label: "LinkedIn",   value: "linkedin"  },
    { label: "Bio",        value: "bio"       },
];

const FIELD_STATUS_FILTER_OPTIONS: { label: string; value: FieldStatus }[] = [
    { label: "Match",     value: "match"    },
    { label: "Mismatch",  value: "mismatch" },
    { label: "WP Only",   value: "wp_only"  },
    { label: "Hub Only",  value: "db_only"  },
    { label: "Missing Both", value: "neither" },
];

// Cached scan results from before field_status existed have no such field at all —
// treat that as "neither" for every field rather than crashing on lookup.
function fieldStatusOf(p: PresenterDiff, field: FieldKey): FieldStatus {
    return p.field_status?.[field] ?? "neither";
}

function rowKeyOf(p: PresenterDiff): string {
    return `${p.name}__${p.sync_status}`;
}

// A row is checkable whenever it has a wp_id — i.e. only_wp, match, and drift
// rows are all actionable (push and/or import); only_db rows have no WP post
// to act on at all.
function isCheckable(p: PresenterDiff): boolean {
    return p.wp_id != null && p.sync_status !== "only_db";
}

// Disabled = checkable in principle but blocked (no email on WP to import with).
function isDisabled(p: PresenterDiff): boolean {
    if (p.sync_status === "only_wp") return p.can_import === false;
    return false;
}

// Eligible for bulk "Select All" — excludes low-confidence name-only matches,
// which must be opted into individually.
function isSelectAllEligible(p: PresenterDiff): boolean {
    if (!isCheckable(p) || isDisabled(p)) return false;
    if (p.match_confidence === "name_only") return false;
    return true;
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ url, name }: { url: string | null; name: string | null }) {
    if (url) {
        // eslint-disable-next-line @next/next/no-img-element
        return <img src={url} alt={name || "presenter"} className="w-14 h-14 rounded-full object-cover border border-[#e2e8f0]" />;
    }
    return (
        <div className="w-14 h-14 rounded-full bg-[#f1f5f9] border border-[#e2e8f0] flex items-center justify-center text-[#94a3b8]">
            <span className="material-symbols-outlined text-2xl">person</span>
        </div>
    );
}

// ─── Expanded detail row ───────────────────────────────────────────────────────

function DetailDrawer({
    p,
    pushState,
    onPush,
    photoPushState,
    onPushPhoto,
}: {
    p: PresenterDiff;
    pushState?: PushState;
    onPush: (p: PresenterDiff) => void;
    photoPushState?: PushState;
    onPushPhoto: (p: PresenterDiff) => void;
}) {
    const isOnlyWP = p.sync_status === "only_wp";
    const isOnlyDB = p.sync_status === "only_db";
    const isDrift = p.sync_status === "drift";

    const pushableDrifted = p.drifted_fields.filter(f => PUSHABLE_FIELDS.includes(f));
    const pushing = pushState?.loading ?? false;
    const photoCfg = PHOTO_STATUS_CONFIG[p.photo_status];
    const photoPushing = photoPushState?.loading ?? false;
    const canPushPhoto = p.photo_status === "hub_only" && !!p.db?.photo_url && p.wp_id != null;

    return (
        <tr>
            <td colSpan={7} className="px-0 py-0">
                <div className="mx-4 mb-4 mt-0.5 rounded-xl border border-[#e2e8f0] overflow-hidden bg-white shadow-sm">
                    {isOnlyWP && (
                        <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-100 text-[12px] text-blue-700 flex items-center gap-2">
                            <span className="material-symbols-outlined text-sm">info</span>
                            This presenter exists on WordPress but has no matching presenter in our VolunteerHub database.
                        </div>
                    )}
                    {isOnlyWP && p.can_import === false && (
                        <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100 text-[12px] text-amber-700 flex items-center gap-2">
                            <span className="material-symbols-outlined text-sm">warning</span>
                            No email on file for this WordPress presenter — an account can&apos;t be created until one is added.
                        </div>
                    )}
                    {isOnlyWP && p.existing_hub_account_id && (
                        <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-100 text-[12px] text-blue-700 flex items-center gap-2">
                            <span className="material-symbols-outlined text-sm">person_search</span>
                            Heads up: this WordPress email matches an existing Hub account that isn&apos;t yet a presenter — importing will link that account rather than create a duplicate.
                        </div>
                    )}
                    {isOnlyDB && (
                        <div className="px-4 py-2.5 bg-rose-50 border-b border-rose-100 text-[12px] text-rose-700 flex items-center gap-2">
                            <span className="material-symbols-outlined text-sm">info</span>
                            This presenter is in our VolunteerHub database but wasn&apos;t found on WordPress.
                        </div>
                    )}

                    {/* Photo comparison */}
                    <div className="px-4 py-3 flex flex-wrap items-center gap-6 border-b border-[#f1f5f9] bg-[#f8fafc]">
                        <div className="flex items-center gap-3">
                            <Avatar url={p.wp?.photo_url ?? null} name={p.wp?.name ?? null} />
                            <span className="text-[10px] font-black uppercase tracking-widest text-[#007a8a]">WordPress</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <Avatar url={p.db?.photo_url ?? null} name={p.db?.name ?? null} />
                            <span className="text-[10px] font-black uppercase tracking-widest text-[#003d45]">VolunteerHub DB</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full ${photoCfg.className}`}>
                                {photoCfg.label}
                            </span>
                            {canPushPhoto && (
                                <button
                                    onClick={() => onPushPhoto(p)}
                                    disabled={photoPushing}
                                    className="flex items-center gap-1.5 bg-[#007a8a] text-white px-3 py-1.5 rounded-lg text-[11px] font-bold hover:bg-[#00636f] transition-colors disabled:opacity-50"
                                >
                                    <span className={`material-symbols-outlined text-sm ${photoPushing ? "animate-spin" : ""}`}>
                                        {photoPushing ? "sync" : "upload"}
                                    </span>
                                    {photoPushing ? "Pushing…" : "Push Photo →"}
                                </button>
                            )}
                            {photoPushState?.error && (
                                <span className="text-[11px] text-rose-600 flex items-center gap-1">
                                    <span className="material-symbols-outlined text-sm">error</span>
                                    {photoPushState.error}
                                </span>
                            )}
                        </div>
                    </div>

                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                                <th className="w-36 px-4 py-2 text-left text-[10px] font-black uppercase tracking-widest text-[#94a3b8]">Field</th>
                                <th className="px-4 py-2 text-left text-[10px] font-black uppercase tracking-widest text-[#007a8a]">WordPress (staging)</th>
                                <th className="px-4 py-2 text-left text-[10px] font-black uppercase tracking-widest text-[#003d45]">VolunteerHub DB</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#f1f5f9]">
                            {FIELD_DEFS.map(({ key, label }) => {
                                const isDrifted = p.drifted_fields.includes(key);
                                const wpVal = p.wp?.[key as keyof PresenterSide] ?? null;
                                const dbVal = p.db?.[key as keyof PresenterSide] ?? null;
                                const isLinkedIn = key === "linkedin";

                                return (
                                    <tr key={key} className={isDrifted ? "bg-amber-50" : ""}>
                                        <td className="px-4 py-2.5 font-semibold text-[#64748b] align-top whitespace-nowrap">
                                            <div className="flex items-center gap-1.5">
                                                {label}
                                                {isDrifted && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2.5 text-[#334155] leading-relaxed align-top break-all">
                                            {wpVal ? (
                                                isLinkedIn ? (
                                                    <a href={wpVal} target="_blank" rel="noreferrer" className="text-[#007a8a] underline">{wpVal}</a>
                                                ) : (
                                                    <span className="whitespace-pre-wrap">{wpVal}</span>
                                                )
                                            ) : <span className="italic text-[#cbd5e1]">not set</span>}
                                        </td>
                                        <td className="px-4 py-2.5 text-[#334155] leading-relaxed align-top break-all">
                                            {dbVal ? (
                                                isLinkedIn ? (
                                                    <a href={dbVal} target="_blank" rel="noreferrer" className="text-[#003d45] underline">{dbVal}</a>
                                                ) : (
                                                    <span className="whitespace-pre-wrap">{dbVal}</span>
                                                )
                                            ) : <span className="italic text-[#cbd5e1]">not set</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {isDrift && (
                        <div className="px-4 py-3 border-t border-[#f1f5f9] bg-[#f8fafc] flex flex-col gap-2">
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => onPush(p)}
                                    disabled={pushing || pushableDrifted.length === 0 || p.wp_id == null}
                                    className="flex items-center gap-2 bg-[#007a8a] text-white px-4 py-2 rounded-lg text-[12px] font-bold hover:bg-[#00636f] transition-colors disabled:opacity-50"
                                >
                                    <span className={`material-symbols-outlined text-sm ${pushing ? "animate-spin" : ""}`}>
                                        {pushing ? "sync" : "upload"}
                                    </span>
                                    {pushing ? "Pushing…" : "Push Hub → WordPress"}
                                </button>
                                <p className="text-[11px] text-[#94a3b8]">
                                    Pushes {pushableDrifted.length > 0 ? pushableDrifted.join(", ") : "field"} to the WordPress
                                    <strong> staging</strong> site only — production is untouched. Use the photo control above to push a photo.
                                </p>
                            </div>
                            {pushState?.error && (
                                <p className="text-[11px] text-rose-600 flex items-center gap-1">
                                    <span className="material-symbols-outlined text-sm">error</span>
                                    {pushState.error}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </td>
        </tr>
    );
}

// ─── Main table ───────────────────────────────────────────────────────────────

function SyncTable({
    presenters,
    selected,
    onToggle,
    onSelectAll,
    pushStates,
    onPush,
    onPushPhoto,
}: {
    presenters: PresenterDiff[];
    selected: Set<number>;
    onToggle: (wpId: number) => void;
    onSelectAll: (checked: boolean, eligibleIds: number[]) => void;
    pushStates: Record<string, PushState>;
    onPush: (p: PresenterDiff) => void;
    onPushPhoto: (p: PresenterDiff) => void;
}) {
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const headerCheckboxRef = useRef<HTMLInputElement>(null);

    function toggle(key: string) {
        setExpandedRow(prev => (prev === key ? null : key));
    }

    const eligibleIds = useMemo(
        () => [...new Set(presenters.filter(isSelectAllEligible).map(p => p.wp_id as number))],
        [presenters]
    );
    const allSelected = eligibleIds.length > 0 && eligibleIds.every(id => selected.has(id));
    const someSelected = eligibleIds.some(id => selected.has(id));

    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
        }
    }, [someSelected, allSelected]);

    return (
        <div className="rounded-xl border border-[#e2e8f0] overflow-hidden bg-white">
            <table className="w-full text-[13px]">
                <thead>
                    <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                        <th className="px-4 py-3 text-left w-8">
                            <input
                                ref={headerCheckboxRef}
                                type="checkbox"
                                checked={allSelected}
                                disabled={eligibleIds.length === 0}
                                onChange={e => onSelectAll(e.target.checked, eligibleIds)}
                                className="w-3.5 h-3.5 accent-[#003d45]"
                                title="Select all importable rows in view"
                            />
                        </th>
                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-[#94a3b8] w-6"></th>
                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-[#94a3b8]">Presenter</th>
                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-[#94a3b8]">Sync</th>
                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-[#94a3b8]">Fields</th>
                        <th className="w-8 px-2"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                    {presenters.length === 0 ? (
                        <tr>
                            <td colSpan={6} className="px-4 py-10 text-center text-[#94a3b8] text-[13px]">
                                No presenters match the current filter.
                            </td>
                        </tr>
                    ) : presenters.map(p => {
                        const key = rowKeyOf(p);
                        const cfg = STATUS_CONFIG[p.sync_status];
                        const isOpen = expandedRow === key;
                        const thumb = p.wp?.photo_url || p.db?.photo_url || null;
                        const checkable = isCheckable(p);
                        const disabled = isDisabled(p);
                        const checked = p.wp_id != null && selected.has(p.wp_id);

                        return (
                            <React.Fragment key={key}>
                                <tr className={`transition-colors hover:bg-[#f8fafc] ${cfg.rowBg}`}>
                                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                        {checkable ? (
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                disabled={disabled}
                                                onChange={() => p.wp_id != null && onToggle(p.wp_id)}
                                                className="w-3.5 h-3.5 accent-[#003d45] disabled:opacity-40"
                                                title={disabled ? "No email on WordPress — cannot create account" : undefined}
                                            />
                                        ) : null}
                                    </td>

                                    <td className="px-4 py-3 cursor-pointer" onClick={() => toggle(key)}>
                                        <span className={`block w-2 h-2 rounded-full ${cfg.dot}`} />
                                    </td>

                                    <td className="px-4 py-3 cursor-pointer" onClick={() => toggle(key)}>
                                        <div className="flex items-center gap-2.5">
                                            {thumb
                                                ? <img src={thumb} alt={p.name} className="w-8 h-8 rounded-full object-cover border border-[#e2e8f0]" />
                                                : <div className="w-8 h-8 rounded-full bg-[#f1f5f9] border border-[#e2e8f0] flex items-center justify-center text-[#94a3b8]">
                                                    <span className="material-symbols-outlined text-sm">person</span>
                                                  </div>}
                                            <span className="font-semibold text-[#1e293b]">{p.name}</span>
                                            {p.match_confidence === "name_only" && (
                                                <span className="text-[9px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#f1f5f9] text-[#94a3b8]">
                                                    name match
                                                </span>
                                            )}
                                        </div>
                                    </td>

                                    <td className="px-4 py-3 cursor-pointer" onClick={() => toggle(key)}>
                                        <span className={`text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full ${cfg.pill}`}>
                                            {cfg.label}
                                        </span>
                                    </td>

                                    <td className="px-4 py-3 cursor-pointer" onClick={() => toggle(key)}>
                                        {p.drifted_fields.length > 0 ? (
                                            <span className="text-[11px] text-amber-600 font-semibold">
                                                {p.drifted_fields.join(", ")}
                                            </span>
                                        ) : (
                                            <span className="text-[#cbd5e1]">—</span>
                                        )}
                                    </td>

                                    <td className="px-2 py-3 text-right cursor-pointer" onClick={() => toggle(key)}>
                                        <span className="material-symbols-outlined text-[#94a3b8] text-base">
                                            {isOpen ? "expand_less" : "expand_more"}
                                        </span>
                                    </td>
                                </tr>

                                {isOpen && (
                                    <DetailDrawer
                                        p={p}
                                        pushState={pushStates[key]}
                                        onPush={onPush}
                                        photoPushState={pushStates[`${key}__photo`]}
                                        onPushPhoto={onPushPhoto}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export function PresenterSyncPanel({ onBack }: { onBack?: () => void } = {}) {
    const [scanning, setScanning] = useState(false);
    const [result, setResult]     = useState<ScanResult | null>(null);
    const [error, setError]       = useState<string | null>(null);

    async function fetchLatest() {
        try {
            const r = await fetchWithAuth("/api/v1/staff/presenter-scan/latest");
            if (r.ok) {
                const data = await r.json();
                if (data) setResult(data);
            }
        } catch {
            // best-effort refresh; ignore
        }
    }

    useEffect(() => {
        fetchLatest();
    }, []);

    const [syncFilters, setSyncFilters] = useState<Set<SyncStatus>>(new Set());
    const [confidenceFilters, setConfidenceFilters] = useState<Set<ConfidenceFilter>>(new Set());
    const [importFilters, setImportFilters] = useState<Set<ImportFilter>>(new Set());
    const [photoStatusFilters, setPhotoStatusFilters] = useState<Set<PhotoStatus>>(new Set());
    const [fieldFilters, setFieldFilters] = useState<Set<FieldKey>>(new Set());
    const [fieldStatusFilters, setFieldStatusFilters] = useState<Set<FieldStatus>>(new Set());
    const [search, setSearch] = useState("");

    const [selectedForImport, setSelectedForImport] = useState<Set<number>>(new Set());
    const [importLoading, setImportLoading] = useState(false);
    const [importSummary, setImportSummary] = useState<ImportResult | null>(null);
    const [pushBulkLoading, setPushBulkLoading] = useState(false);
    const [pushSummary, setPushSummary] = useState<BulkPushSummary | null>(null);

    const [pushedOverrides, setPushedOverrides] = useState<Record<string, PushOverride>>({});
    const [pushStates, setPushStates] = useState<Record<string, PushState>>({});

    function toggleSync(v: SyncStatus) {
        setSyncFilters(prev => {
            const next = new Set(prev);
            next.has(v) ? next.delete(v) : next.add(v);
            return next;
        });
    }

    function toggleConfidence(v: ConfidenceFilter) {
        setConfidenceFilters(prev => {
            const next = new Set(prev);
            next.has(v) ? next.delete(v) : next.add(v);
            return next;
        });
    }

    function toggleImportFilter(v: ImportFilter) {
        setImportFilters(prev => {
            const next = new Set(prev);
            next.has(v) ? next.delete(v) : next.add(v);
            return next;
        });
    }

    function togglePhotoStatusFilter(v: PhotoStatus) {
        setPhotoStatusFilters(prev => {
            const next = new Set(prev);
            next.has(v) ? next.delete(v) : next.add(v);
            return next;
        });
    }

    function toggleFieldFilter(v: FieldKey) {
        setFieldFilters(prev => {
            const next = new Set(prev);
            next.has(v) ? next.delete(v) : next.add(v);
            return next;
        });
    }

    function toggleFieldStatusFilter(v: FieldStatus) {
        setFieldStatusFilters(prev => {
            const next = new Set(prev);
            next.has(v) ? next.delete(v) : next.add(v);
            return next;
        });
    }

    function toggleSelected(wpId: number) {
        setSelectedForImport(prev => {
            const next = new Set(prev);
            next.has(wpId) ? next.delete(wpId) : next.add(wpId);
            return next;
        });
    }

    function handleSelectAll(checked: boolean, eligibleIds: number[]) {
        setSelectedForImport(prev => {
            const next = new Set(prev);
            if (checked) {
                eligibleIds.forEach(id => next.add(id));
            } else {
                eligibleIds.forEach(id => next.delete(id));
            }
            return next;
        });
    }

    async function runScan() {
        setScanning(true);
        setError(null);
        try {
            const res = await fetchWithAuth("/api/v1/staff/presenter-scan", { method: "POST" });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || `HTTP ${res.status}`);
            }
            setResult(await res.json());
            setPushedOverrides({});
            setPushStates({});
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Scan failed");
        } finally {
            setScanning(false);
        }
    }

    async function importSelected() {
        if (selectedForImport.size === 0) return;
        setImportLoading(true);
        try {
            const res = await fetchWithAuth("/api/v1/staff/presenter-scan/import", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ wp_ids: [...selectedForImport] }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || `HTTP ${res.status}`);
            }
            const data: ImportResult = await res.json();
            setImportSummary(data);
            setSelectedForImport(new Set());
            await fetchLatest();
        } catch (e: unknown) {
            setImportSummary({
                created: 0,
                updated: 0,
                skipped: 0,
                results: [{ wp_id: 0, name: "Import request", action: "skipped", reason: e instanceof Error ? e.message : "Import failed" }],
            });
        } finally {
            setImportLoading(false);
        }
    }

    async function handlePush(p: PresenterDiff) {
        const key = rowKeyOf(p);
        if (p.wp_id == null) return;
        const fields = p.drifted_fields.filter(f => PUSHABLE_FIELDS.includes(f));
        if (fields.length === 0) return;

        const values: Record<string, string> = {};
        for (const f of fields) {
            values[f] = p.db?.[f as keyof PresenterSide] ?? "";
        }

        setPushStates(prev => ({ ...prev, [key]: { loading: true, error: null } }));
        try {
            const res = await fetchWithAuth("/api/v1/staff/presenter-scan/push-to-wp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ wp_id: p.wp_id, fields, values }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || `HTTP ${res.status}`);
            }
            const snapshot: PresenterSide = await res.json();
            setPushedOverrides(prev => ({
                ...prev,
                [key]: { wp: snapshot, clearedFields: fields, photoStatus: prev[key]?.photoStatus },
            }));
            setPushStates(prev => ({ ...prev, [key]: { loading: false, error: null } }));
        } catch (e: unknown) {
            setPushStates(prev => ({
                ...prev,
                [key]: { loading: false, error: e instanceof Error ? e.message : "Push failed" },
            }));
        }
    }

    async function handlePushPhoto(p: PresenterDiff) {
        const baseKey = rowKeyOf(p);
        const stateKey = `${baseKey}__photo`;
        if (p.wp_id == null || !p.db?.photo_url) return;

        setPushStates(prev => ({ ...prev, [stateKey]: { loading: true, error: null } }));
        try {
            const res = await fetchWithAuth("/api/v1/staff/presenter-scan/push-photo", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ wp_id: p.wp_id, photo_url: p.db.photo_url }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || `HTTP ${res.status}`);
            }
            const snapshot: PresenterSide = await res.json();
            setPushedOverrides(prev => ({
                ...prev,
                [baseKey]: {
                    wp: { ...(prev[baseKey]?.wp ?? {}), ...snapshot },
                    clearedFields: prev[baseKey]?.clearedFields ?? [],
                    photoStatus: "both",
                },
            }));
            setPushStates(prev => ({ ...prev, [stateKey]: { loading: false, error: null } }));
        } catch (e: unknown) {
            setPushStates(prev => ({
                ...prev,
                [stateKey]: { loading: false, error: e instanceof Error ? e.message : "Push failed" },
            }));
        }
    }

    const enrichedPresenters = useMemo(() => {
        return (result?.presenters ?? []).map(p => {
            const override = pushedOverrides[rowKeyOf(p)];
            if (!override) return p;
            return {
                ...p,
                wp: p.wp ? { ...p.wp, ...override.wp } : p.wp,
                drifted_fields: p.drifted_fields.filter(f => !override.clearedFields.includes(f)),
                photo_status: override.photoStatus ?? p.photo_status,
            };
        });
    }, [result, pushedOverrides]);

    const filtered = enrichedPresenters.filter(p => {
        if (syncFilters.size > 0 && !syncFilters.has(p.sync_status)) return false;
        if (confidenceFilters.size > 0 && !(p.match_confidence && confidenceFilters.has(p.match_confidence))) return false;
        if (importFilters.size > 0) {
            // can_import is only meaningful for only_wp rows — for everything else the
            // backend sends it as null (not undefined), so check for that explicitly
            // rather than falling through to a false-y "no_email" bucket.
            if (p.can_import == null) return false;
            const bucket: ImportFilter = p.can_import ? "has_email" : "no_email";
            if (!importFilters.has(bucket)) return false;
        }
        if (photoStatusFilters.size > 0 && !photoStatusFilters.has(p.photo_status)) return false;
        if (fieldFilters.size > 0) {
            const matchesField = [...fieldFilters].some(f => {
                const status = fieldStatusOf(p, f);
                return fieldStatusFilters.size > 0 ? fieldStatusFilters.has(status) : status !== "neither";
            });
            if (!matchesField) return false;
        }
        if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    const selectedRows = enrichedPresenters.filter(p => p.wp_id != null && selectedForImport.has(p.wp_id));
    const pushEligibleTextRows = selectedRows.filter(
        p => p.sync_status === "drift" && p.drifted_fields.some(f => PUSHABLE_FIELDS.includes(f))
    );
    const pushEligiblePhotoRows = selectedRows.filter(p => p.photo_status === "hub_only" && !!p.db?.photo_url);
    const canPushSelected = pushEligibleTextRows.length > 0 || pushEligiblePhotoRows.length > 0;

    async function pushSelected() {
        if (!canPushSelected) return;
        setPushBulkLoading(true);
        try {
            const results: BulkPushResultItem[] = [];

            if (pushEligibleTextRows.length > 0) {
                const body = pushEligibleTextRows.map(p => {
                    const fields = p.drifted_fields.filter(f => PUSHABLE_FIELDS.includes(f));
                    const values: Record<string, string> = {};
                    for (const f of fields) {
                        values[f] = p.db?.[f as keyof PresenterSide] ?? "";
                    }
                    return { wp_id: p.wp_id as number, fields, values };
                });
                try {
                    const res = await fetchWithAuth("/api/v1/staff/presenter-scan/push-to-wp/bulk", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(body),
                    });
                    if (!res.ok) {
                        const data = await res.json().catch(() => ({}));
                        throw new Error(data.detail || `HTTP ${res.status}`);
                    }
                    const data: { results: BulkPushResultItem[] } = await res.json();
                    results.push(...data.results);
                } catch (e: unknown) {
                    const detail = e instanceof Error ? e.message : "Push failed";
                    results.push(...body.map(b => ({ wp_id: b.wp_id, status: "error" as const, detail })));
                }
            }

            if (pushEligiblePhotoRows.length > 0) {
                const body = pushEligiblePhotoRows.map(p => ({ wp_id: p.wp_id as number, photo_url: p.db?.photo_url ?? "" }));
                try {
                    const res = await fetchWithAuth("/api/v1/staff/presenter-scan/push-photo/bulk", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(body),
                    });
                    if (!res.ok) {
                        const data = await res.json().catch(() => ({}));
                        throw new Error(data.detail || `HTTP ${res.status}`);
                    }
                    const data: { results: BulkPushResultItem[] } = await res.json();
                    results.push(...data.results);
                } catch (e: unknown) {
                    const detail = e instanceof Error ? e.message : "Push failed";
                    results.push(...body.map(b => ({ wp_id: b.wp_id, status: "error" as const, detail })));
                }
            }

            const nameById = new Map(selectedRows.map(p => [p.wp_id as number, p.name]));
            const summary: BulkPushSummary = {
                ok: results.filter(r => r.status === "ok").length,
                error: results.filter(r => r.status === "error").length,
                errors: results
                    .filter(r => r.status === "error")
                    .map(r => ({ wp_id: r.wp_id, name: nameById.get(r.wp_id) ?? String(r.wp_id), detail: r.detail })),
            };
            setPushSummary(summary);
            setSelectedForImport(new Set());
            await fetchLatest();
        } finally {
            setPushBulkLoading(false);
        }
    }

    const anyFiltersActive = syncFilters.size > 0 || confidenceFilters.size > 0 || importFilters.size > 0
        || photoStatusFilters.size > 0 || fieldFilters.size > 0 || fieldStatusFilters.size > 0;

    const scannedAt = result ? new Date(result.scanned_at).toLocaleString() : null;

    return (
        <div className="flex flex-col gap-5 p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="mt-0.5 flex items-center gap-1 text-[#64748b] hover:text-[#003d45] transition-colors text-[13px] font-semibold"
                        >
                            <span className="material-symbols-outlined text-base">arrow_back</span>
                            Back
                        </button>
                    )}
                    <div>
                        <h2 className="text-xl font-black text-[#003d45]">Presenter Sync</h2>
                        <p className="text-[13px] text-[#64748b] mt-0.5">
                            Compare VolunteerHub presenters with WordPress (staging) — photos, LinkedIn, bio, job title, company
                        </p>
                        {scannedAt && (
                            <p className="text-[11px] text-[#94a3b8] mt-1">Last scanned: {scannedAt}</p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        onClick={runScan}
                        disabled={scanning}
                        className="flex items-center gap-2 bg-[#003d45] text-white px-5 py-2.5 rounded-xl text-[13px] font-bold hover:bg-[#005060] transition-colors disabled:opacity-60"
                    >
                        <span className={`material-symbols-outlined text-base ${scanning ? "animate-spin" : ""}`}>
                            {scanning ? "sync" : "travel_explore"}
                        </span>
                        {scanning ? "Scanning…" : "Run Live Scan"}
                    </button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-[13px] text-rose-700">
                    <strong>Scan failed:</strong> {error}
                </div>
            )}

            {/* Loading */}
            {scanning && (
                <div className="flex flex-col items-center gap-3 py-16 text-[#64748b]">
                    <div className="w-10 h-10 border-2 border-[#003d45]/20 border-t-[#003d45] rounded-full animate-spin" />
                    <p className="text-[13px] font-semibold">Fetching WordPress presenters and comparing…</p>
                    <p className="text-[11px] text-[#94a3b8]">~1,400 records — this may take a bit longer than the group scan</p>
                </div>
            )}

            {/* Results */}
            {result && !scanning && (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                        {[
                            { label: "WordPress",  value: result.summary.total_wp, color: "text-[#003d45]" },
                            { label: "Our DB",      value: result.summary.total_db, color: "text-[#003d45]" },
                            { label: "Matched",     value: result.summary.matched,  color: "text-emerald-600" },
                            { label: "Field Drift", value: result.summary.drift,    color: "text-amber-600" },
                            { label: "WP Only",     value: result.summary.only_wp,  color: "text-blue-600" },
                            { label: "DB Only",     value: result.summary.only_db,  color: "text-rose-600" },
                        ].map(c => (
                            <div key={c.label} className="bg-white border border-[#e2e8f0] rounded-xl px-4 py-3">
                                <p className={`text-2xl font-black ${c.color}`}>{c.value}</p>
                                <p className="text-[10px] font-semibold text-[#94a3b8] uppercase tracking-wide mt-0.5">{c.label}</p>
                            </div>
                        ))}
                    </div>

                    {result.summary.in_sync ? (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 flex items-center gap-2 text-emerald-700 text-[13px] font-semibold">
                            <span className="material-symbols-outlined text-base">check_circle</span>
                            All presenters are in sync with WordPress.
                        </div>
                    ) : (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-2 text-amber-700 text-[13px] font-semibold">
                            <span className="material-symbols-outlined text-base">warning</span>
                            {result.summary.drift + result.summary.only_wp + result.summary.only_db} presenter(s) need attention.
                        </div>
                    )}

                    {/* Import summary banner */}
                    {importSummary && (
                        <div className="bg-white border border-[#e2e8f0] rounded-xl px-4 py-3 text-[13px] text-[#334155] relative">
                            <button
                                onClick={() => setImportSummary(null)}
                                className="absolute top-2.5 right-2.5 text-[#94a3b8] hover:text-[#003d45] transition-colors"
                            >
                                <span className="material-symbols-outlined text-base">close</span>
                            </button>
                            <p className="font-bold text-[#003d45]">
                                Created {importSummary.created}, Updated {importSummary.updated}, Skipped {importSummary.skipped} — see details
                            </p>
                            {importSummary.results.some(r => r.action === "skipped") && (
                                <ul className="mt-2 space-y-1 text-[12px] text-[#64748b] list-disc list-inside">
                                    {importSummary.results.filter(r => r.action === "skipped").map((r, i) => (
                                        <li key={`${r.wp_id}-${i}`}>
                                            {r.name}{r.reason ? `: ${r.reason}` : ""}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {/* Push summary banner */}
                    {pushSummary && (
                        <div className="bg-white border border-[#e2e8f0] rounded-xl px-4 py-3 text-[13px] text-[#334155] relative">
                            <button
                                onClick={() => setPushSummary(null)}
                                className="absolute top-2.5 right-2.5 text-[#94a3b8] hover:text-[#003d45] transition-colors"
                            >
                                <span className="material-symbols-outlined text-base">close</span>
                            </button>
                            <p className="font-bold text-[#003d45]">
                                Pushed {pushSummary.ok} OK, {pushSummary.error} error{pushSummary.error === 1 ? "" : "s"}
                            </p>
                            {pushSummary.errors.length > 0 && (
                                <ul className="mt-2 space-y-1 text-[12px] text-rose-600 list-disc list-inside">
                                    {pushSummary.errors.map((e, i) => (
                                        <li key={`${e.wp_id}-${i}`}>
                                            {e.name}{e.detail ? `: ${e.detail}` : ""}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {/* Top action bar — selection-driven bulk actions */}
                    {selectedForImport.size > 0 && (
                        <div className="bg-[#003d45] text-white rounded-xl px-5 py-3 flex items-center justify-between flex-wrap gap-3 shadow-sm">
                            <div className="flex items-center gap-3">
                                <span className="text-[13px] font-bold">{selectedForImport.size} selected</span>
                                <button
                                    onClick={() => setSelectedForImport(new Set())}
                                    className="text-[11px] text-white/70 hover:text-white font-semibold transition-colors"
                                >
                                    Clear
                                </button>
                            </div>
                            <div className="flex items-center gap-2">
                                {canPushSelected && (
                                    <button
                                        onClick={pushSelected}
                                        disabled={pushBulkLoading}
                                        className="flex items-center gap-2 bg-white/10 border border-white/30 text-white px-4 py-2 rounded-lg text-[12px] font-bold hover:bg-white/20 transition-colors disabled:opacity-60"
                                    >
                                        <span className={`material-symbols-outlined text-sm ${pushBulkLoading ? "animate-spin" : ""}`}>
                                            {pushBulkLoading ? "sync" : "upload"}
                                        </span>
                                        {pushBulkLoading ? "Pushing…" : "Push Selected → WordPress"}
                                    </button>
                                )}
                                <button
                                    onClick={importSelected}
                                    disabled={importLoading}
                                    className="flex items-center gap-2 bg-white text-[#003d45] px-4 py-2 rounded-lg text-[12px] font-bold hover:bg-[#f1f5f9] transition-colors disabled:opacity-60"
                                >
                                    <span className={`material-symbols-outlined text-sm ${importLoading ? "animate-spin" : ""}`}>
                                        {importLoading ? "sync" : "person_add"}
                                    </span>
                                    {importLoading ? "Importing…" : "Import Selected"}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Filters + search */}
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-4 flex-wrap">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-black uppercase tracking-widest text-[#94a3b8] mr-1">Sync</span>
                                {SYNC_FILTER_OPTIONS.map(opt => {
                                    const active = syncFilters.has(opt.value);
                                    const cfg = STATUS_CONFIG[opt.value];
                                    return (
                                        <button
                                            key={opt.value}
                                            onClick={() => toggleSync(opt.value)}
                                            className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wide transition-colors
                                                ${active
                                                    ? "bg-[#003d45] text-white"
                                                    : "bg-white border border-[#e2e8f0] text-[#64748b] hover:border-[#003d45]"}`}
                                        >
                                            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${active ? "bg-white/70" : cfg.dot}`} />
                                            {opt.label}
                                            <span className="ml-1 opacity-60">
                                                ({result.presenters.filter(p => p.sync_status === opt.value).length})
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-black uppercase tracking-widest text-[#94a3b8] mr-1">Confidence</span>
                                {CONFIDENCE_FILTER_OPTIONS.map(opt => {
                                    const active = confidenceFilters.has(opt.value);
                                    return (
                                        <button
                                            key={opt.value}
                                            onClick={() => toggleConfidence(opt.value)}
                                            className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wide transition-colors
                                                ${active
                                                    ? "bg-[#003d45] text-white"
                                                    : "bg-white border border-[#e2e8f0] text-[#64748b] hover:border-[#003d45]"}`}
                                        >
                                            {opt.label}
                                            <span className="ml-1 opacity-60">
                                                ({result.presenters.filter(p => p.match_confidence === opt.value).length})
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-black uppercase tracking-widest text-[#94a3b8] mr-1">Import Readiness</span>
                                {IMPORT_FILTER_OPTIONS.map(opt => {
                                    const active = importFilters.has(opt.value);
                                    return (
                                        <button
                                            key={opt.value}
                                            onClick={() => toggleImportFilter(opt.value)}
                                            className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wide transition-colors
                                                ${active
                                                    ? "bg-[#003d45] text-white"
                                                    : "bg-white border border-[#e2e8f0] text-[#64748b] hover:border-[#003d45]"}`}
                                        >
                                            {opt.label}
                                            <span className="ml-1 opacity-60">
                                                ({result.presenters.filter(p => (opt.value === "has_email" ? p.can_import === true : p.can_import === false)).length})
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-black uppercase tracking-widest text-[#94a3b8] mr-1">Photo</span>
                                {PHOTO_STATUS_FILTER_OPTIONS.map(opt => {
                                    const active = photoStatusFilters.has(opt.value);
                                    return (
                                        <button
                                            key={opt.value}
                                            onClick={() => togglePhotoStatusFilter(opt.value)}
                                            className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wide transition-colors
                                                ${active
                                                    ? "bg-[#003d45] text-white"
                                                    : "bg-white border border-[#e2e8f0] text-[#64748b] hover:border-[#003d45]"}`}
                                        >
                                            {opt.label}
                                            <span className="ml-1 opacity-60">
                                                ({result.presenters.filter(p => p.photo_status === opt.value).length})
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-black uppercase tracking-widest text-[#94a3b8] mr-1">Field</span>
                                {FIELD_FILTER_OPTIONS.map(opt => {
                                    const active = fieldFilters.has(opt.value);
                                    const count = result.presenters.filter(p => {
                                        const status = fieldStatusOf(p, opt.value);
                                        return fieldStatusFilters.size > 0 ? fieldStatusFilters.has(status) : status !== "neither";
                                    }).length;
                                    return (
                                        <button
                                            key={opt.value}
                                            onClick={() => toggleFieldFilter(opt.value)}
                                            className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wide transition-colors
                                                ${active
                                                    ? "bg-[#003d45] text-white"
                                                    : "bg-white border border-[#e2e8f0] text-[#64748b] hover:border-[#003d45]"}`}
                                        >
                                            {opt.label}
                                            <span className="ml-1 opacity-60">({count})</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {fieldFilters.size > 0 && (
                                <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-[#94a3b8] mr-1">Field Status</span>
                                    {FIELD_STATUS_FILTER_OPTIONS.map(opt => {
                                        const active = fieldStatusFilters.has(opt.value);
                                        return (
                                            <button
                                                key={opt.value}
                                                onClick={() => toggleFieldStatusFilter(opt.value)}
                                                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wide transition-colors
                                                    ${active
                                                        ? "bg-[#003d45] text-white"
                                                        : "bg-white border border-[#e2e8f0] text-[#64748b] hover:border-[#003d45]"}`}
                                            >
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="flex-1" />

                            {anyFiltersActive && (
                                <button
                                    onClick={() => {
                                        setSyncFilters(new Set());
                                        setConfidenceFilters(new Set());
                                        setImportFilters(new Set());
                                        setPhotoStatusFilters(new Set());
                                        setFieldFilters(new Set());
                                        setFieldStatusFilters(new Set());
                                    }}
                                    className="text-[11px] text-[#94a3b8] hover:text-[#003d45] font-semibold transition-colors"
                                >
                                    Clear filters
                                </button>
                            )}

                            <input
                                type="text"
                                placeholder="Search presenters…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="border border-[#e2e8f0] rounded-lg px-3 py-1.5 text-[13px] text-[#334155] outline-none focus:border-[#003d45] w-52"
                            />
                        </div>

                        {anyFiltersActive && (
                            <p className="text-[11px] text-[#64748b]">
                                Showing {filtered.length} of {result.presenters.length} presenters
                            </p>
                        )}
                    </div>

                    <SyncTable
                        presenters={filtered}
                        selected={selectedForImport}
                        onToggle={toggleSelected}
                        onSelectAll={handleSelectAll}
                        pushStates={pushStates}
                        onPush={handlePush}
                        onPushPhoto={handlePushPhoto}
                    />
                </>
            )}

            {/* Empty state */}
            {!result && !scanning && !error && (
                <div className="flex flex-col items-center gap-3 py-20 text-[#94a3b8]">
                    <span className="material-symbols-outlined text-5xl">travel_explore</span>
                    <p className="text-[14px] font-semibold">Click &ldquo;Run Live Scan&rdquo; to compare presenters</p>
                    <p className="text-[12px]">Pulls all presenters from the WordPress staging API and diffs against our database</p>
                </div>
            )}
        </div>
    );
}
